import every from "lodash/every";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import isObject from "lodash/isObject";
import pickBy from "lodash/pickBy";
import some from "lodash/some";
import values from "lodash/values";
import { isValueEmpty } from "PFCore/helpers/filters/is_value_empty";
import { omitUndefined } from "PFCore/helpers/object";
import { FiltersV2, FilterV2, FilterV2WithValueOnly, FilterValueV2 } from "PFTypes/filters_v2";

export const hasDefaultValue = ({ defaultValue }: Pick<FilterV2, "defaultValue">) =>
  defaultValue !== undefined && !isValueEmpty(defaultValue);

export const hasAnyDefaultValue = (filters: FiltersV2) => some(values(filters), hasDefaultValue);

export const hasAnyError = (filters: FiltersV2) =>
  some(values(filters), ({ error }) => !!error && error.length > 0);

export const areValuesEqual = (value1?: FilterValueV2, value2?: FilterValueV2) => {
  if (isArray(value1) && value1.length === 1 && !isArray(value2)) {
    return isEqual(value1[0], value2);
  }

  if (isArray(value2) && value2.length === 1 && !isArray(value1)) {
    return isEqual(value2[0], value1);
  }

  if (isObject(value1) && isObject(value2)) {
    return isEqual(omitUndefined(value1), omitUndefined(value2));
  }

  return (isValueEmpty(value1) && isValueEmpty(value2)) || isEqual(value1, value2);
};

export const isFilterPristine = ({ value, defaultValue }: Pick<FilterV2, "defaultValue" | "value">) =>
  (isValueEmpty(value) && !hasDefaultValue({ defaultValue })) || areValuesEqual(value, defaultValue);

export const areFiltersPristine = (filters: FiltersV2) => every(values(filters), isFilterPristine);

export const isFilterModified = (filter: Pick<FilterV2, "defaultValue" | "value">) =>
  !isFilterPristine(filter);

export const canFilterBeRestored = (filter: Pick<FilterV2, "defaultValue" | "value">) =>
  isFilterModified(filter) && hasDefaultValue(filter);

export const areFiltersModified = (filters: FiltersV2 | FilterV2[]) =>
  some(isArray(filters) ? filters : values(filters), isFilterModified);

export const areFilterValuesEmpty = (filters: FiltersV2<FilterV2WithValueOnly>) =>
  every(values(filters), ({ value }) => isValueEmpty(value));

export const areNonRequiredFilterValuesEmpty = (filters: FiltersV2) =>
  every(
    values(filters).filter((filter) => !filter.required),
    ({ value }) => isValueEmpty(value)
  );

export const areFiltersEqual = <
  T extends FilterV2 | FilterV2WithValueOnly = FilterV2,
  K extends FilterV2 | FilterV2WithValueOnly = FilterV2
>(
  filters1: FiltersV2<T>,
  filters2: FiltersV2<K>
) =>
  every(filters1, (filter, filterKey) => areValuesEqual(filter.value, filters2[filterKey]?.value)) &&
  every(filters2, (filter, filterKey) => areValuesEqual(filter.value, filters1[filterKey]?.value));

export const getFiltersWithDifferentValues = <
  T extends FilterV2 | FilterV2WithValueOnly = FilterV2,
  K extends FilterV2 | FilterV2WithValueOnly = FilterV2
>(
  filters1: FiltersV2<T>,
  filters2: FiltersV2<K>
) =>
  values(
    pickBy(
      filters1,
      (updatedFilter, updatedFilterKey) =>
        !areValuesEqual(updatedFilter.value, filters2[updatedFilterKey]?.value)
    )
  );
